/**
 * useThreadNavigation — Thread message navigation and filtering
 *
 * Extracted from MainWorkspace to reduce component size.
 * Manages: thread mode detection, role filtering, message-level navigation,
 * display text computation with media ref injection.
 *
 * @module @humanizer/studio/hooks/useThreadNavigation
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useRoleFilters } from '../components/workspace/RoleFilter';
import type { ContentItem } from '../contexts/BufferSyncContext';
import type { WorkspaceContent, ViewMode } from '../components/workspace/workspace-types';
import { injectMediaRefs } from '../utils/media-url';

export interface UseThreadNavigationOptions {
  /** Messages array (thread mode if >1) */
  messages: ContentItem[] | undefined;
  /** Active content item */
  content: WorkspaceContent | null | undefined;
  /** Draft text for focusedText logic */
  draftText: string;
  /** Current view mode */
  viewMode: ViewMode;
  /** Stack items for focusedText in stack mode */
  stackItems: Array<{ contentItem?: { text?: string } | null }>;
  /** Active stack index */
  activeStackIndex: number | null;
  /** Set focused text for tools pane */
  setFocusedText: (text: string | null) => void;
  /** Navigate to specific message index (from search) */
  initialMessageIndex: number | undefined;
  /** Clear pending navigation after consuming */
  onClearPendingNavigation?: () => void;
}

export interface UseThreadNavigationReturn {
  isThreadMode: boolean;
  activeMessageIndex: number;
  filters: { showUser: boolean; showAssistant: boolean; showSystem: boolean; showTool: boolean };
  setFilters: (filters: { showUser: boolean; showAssistant: boolean; showSystem: boolean; showTool: boolean }) => void;
  filteredIndices: number[];
  currentMessage: ContentItem | null;
  currentFilteredPos: number;
  displayText: string;
  displayRole: string | undefined;
  goFirst: () => void;
  goPrev: () => void;
  goNext: () => void;
  goLast: () => void;
  jumpToMessage: (index: number) => void;
}

export function useThreadNavigation({
  messages,
  content,
  draftText,
  viewMode,
  stackItems,
  activeStackIndex,
  setFocusedText,
  initialMessageIndex,
  onClearPendingNavigation,
}: UseThreadNavigationOptions): UseThreadNavigationReturn {
  const isThreadMode = messages !== undefined && messages.length > 1;
  const [activeMessageIndex, setActiveMessageIndex] = useState(0);
  const { filters, setFilters } = useRoleFilters();

  // Reset message index when messages change
  useEffect(() => {
    setActiveMessageIndex(0);
  }, [messages?.length]);

  // Navigate to specific message when requested externally
  useEffect(() => {
    if (initialMessageIndex !== undefined && initialMessageIndex !== null && messages && initialMessageIndex < messages.length) {
      setActiveMessageIndex(initialMessageIndex);
    }
  }, [initialMessageIndex, messages]);

  // Clear pending navigation after consuming
  useEffect(() => {
    if (initialMessageIndex !== undefined && initialMessageIndex !== null) {
      const timer = setTimeout(() => onClearPendingNavigation?.(), 100);
      return () => clearTimeout(timer);
    }
  }, [initialMessageIndex, onClearPendingNavigation]);

  // Set focusedText for tools pane based on what the user is actually viewing
  useEffect(() => {
    if (isThreadMode && messages && messages[activeMessageIndex]) {
      setFocusedText(messages[activeMessageIndex].text);
    } else if (viewMode === 'stack' && stackItems.length > 0) {
      const idx = activeStackIndex ?? 0;
      const activeItem = stackItems[idx];
      setFocusedText(activeItem?.contentItem?.text ?? null);
    } else if ((viewMode === 'draft' || viewMode === 'split') && draftText) {
      setFocusedText(draftText);
    } else if (viewMode === 'read' || viewMode === 'edit') {
      setFocusedText(content?.text || null);
    } else {
      setFocusedText(null);
    }
  }, [isThreadMode, messages, activeMessageIndex, setFocusedText, viewMode, stackItems, activeStackIndex, draftText, content?.text]);

  // Filtered indices based on role filters
  const filteredIndices = useMemo(() => {
    if (!messages) return [];
    return messages
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => {
        const role = m.metadata.authorRole;
        if (role === 'user' && !filters.showUser) return false;
        if (role === 'assistant' && !filters.showAssistant) return false;
        if (role === 'system' && !filters.showSystem) return false;
        if (role === 'tool' && !filters.showTool) return false;
        return true;
      })
      .map(({ i }) => i);
  }, [messages, filters]);

  const currentMessage = isThreadMode ? messages[activeMessageIndex] : null;
  const currentFilteredPos = filteredIndices.indexOf(activeMessageIndex);

  const goFirst = useCallback(() => {
    if (filteredIndices.length > 0) setActiveMessageIndex(filteredIndices[0]);
  }, [filteredIndices]);

  const goPrev = useCallback(() => {
    if (currentFilteredPos > 0) {
      setActiveMessageIndex(filteredIndices[currentFilteredPos - 1]);
    }
  }, [filteredIndices, currentFilteredPos]);

  const goNext = useCallback(() => {
    if (currentFilteredPos < filteredIndices.length - 1) {
      setActiveMessageIndex(filteredIndices[currentFilteredPos + 1]);
    }
  }, [filteredIndices, currentFilteredPos]);

  const goLast = useCallback(() => {
    if (filteredIndices.length > 0) {
      setActiveMessageIndex(filteredIndices[filteredIndices.length - 1]);
    }
  }, [filteredIndices]);

  const jumpToMessage = useCallback((index: number) => {
    setActiveMessageIndex(index);
  }, []);

  // Display text with media ref injection
  const displayText = useMemo(() => {
    const rawText = isThreadMode && currentMessage
      ? currentMessage.text
      : content?.text ?? '';
    const mediaRefs = isThreadMode && currentMessage
      ? currentMessage.metadata?.mediaRefs as Array<{ id: string; type: string }> | undefined
      : undefined;
    return injectMediaRefs(rawText, mediaRefs);
  }, [isThreadMode, currentMessage, content?.text]);

  const displayRole = isThreadMode && currentMessage
    ? currentMessage.metadata.authorRole
    : content?.metadata?.authorRole;

  return {
    isThreadMode,
    activeMessageIndex,
    filters,
    setFilters,
    filteredIndices,
    currentMessage,
    currentFilteredPos,
    displayText,
    displayRole,
    goFirst,
    goPrev,
    goNext,
    goLast,
    jumpToMessage,
  };
}
